import { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Checkbox,
  Radio,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchSummaryReport, approveExpense } from '../../api/travelActivityApi';
import { toast } from 'react-toastify';
import MantisDataTable from '../../components/common/MantisDataTable';
import { useAuth } from '../../sections/auth/AuthContext';
import { calculateTotals } from '../../utils/common';

// Base image URL from environment variable
const BASE_IMAGE_URL = (import.meta.env.VITE_BASE_IMAGE_URL) || '';

// Helper to generate last N calendar months
function generateMonthRanges(monthsBack = 2) {
  const ranges = [];
  const today = new Date();

  for (let i = 1; i < monthsBack; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth(); // 0-indexed
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const start = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const end = `${year}-${String(month + 1).padStart(2, "0")}-${daysInMonth}`;

    ranges.push({
      label: `${start} - ${end}`,
      value: `${start},${end}`
    });
  }

  return ranges;
}

const AdminApproval = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const loggedInUser = userInfo.empCode;
  const [form, setForm] = useState({
    startDate: '',
    endDate: '',
    emp_code: loggedInUser,
    action: 'summary',
    emp_status: 'ACTIVE',
    quickRange: ''
  });

  const [summaryData, setSummaryData] = useState([]);
  const [approvalRows, setApprovalRows] = useState([]);
  const [showDetailTable, setShowDetailTable] = useState(false);
  const [dateRanges, setDateRanges] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Image popup state
  const [imagePopup, setImagePopup] = useState({
    open: false,
    imageUrl: null,
    title: ''
  });

  // Generate date ranges on component mount
  useEffect(() => {
    setDateRanges(generateMonthRanges(3));
  }, []);

  // Helper function to build full image URL
  const getFullImageUrl = (filename) => {
    if (!filename || filename === 'null') return null;
    
    // If filename already contains http or https, return as is
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }
    
    // Otherwise, concatenate with base URL
    // Ensure base URL ends with slash and filename doesn't start with slash
    const baseUrl = BASE_IMAGE_URL.endsWith('/') ? BASE_IMAGE_URL : `${BASE_IMAGE_URL}/`;
    const cleanFilename = filename.startsWith('/') ? filename.slice(1) : filename;
    
    return `${baseUrl}${cleanFilename}`;
  };

  const handleFilterChange = (filters) => {
    setForm({ ...form, ...filters });
    
    // If quickRange is selected, extract start and end dates
    if (filters.quickRange) {
      const [startDate, endDate] = filters.quickRange.split(",");
      setForm(prev => ({ ...prev, startDate, endDate }));
    }
  };

  const buildSummaryPayload = (payloadSource) => {
    return {
      startDate: payloadSource.startDate ?? '',
      endDate: payloadSource.endDate ?? '',
      emp_code: loggedInUser,
      action: 'summary',
      company_code: userInfo.company_code,
      emp_status: form.emp_status   // 👈 ADD THIS
    };
  };

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        // Set current month as default
        const ranges = generateMonthRanges(1);
        if (ranges.length > 0) {
          const [startDate, endDate] = ranges[0].value.split(",");
          
          setForm(prev => ({
            ...prev,
            quickRange: ranges[0].value,
            startDate,
            endDate
          }));

          const payload = buildSummaryPayload({ startDate, endDate });
          const resp = await fetchSummaryReport(payload);
          setSummaryData(resp || []);
        }
      } catch (err) {
        console.error('Error fetching initial summary', err);
        setSummaryData([]);
      }
    };
    fetchInitial();
  }, []);

  const handleFilterSubmit = async () => {
    try {
      let startDate = form.startDate;
      let endDate = form.endDate;

      if (form.quickRange) {
        [startDate, endDate] = form.quickRange.split(",");
      }

      setForm(prev => ({ ...prev, startDate, endDate }));

      const payload = buildSummaryPayload({ startDate, endDate });
      const resp = await fetchSummaryReport(payload);
      setSummaryData(resp || []);
      setShowDetailTable(false);
      setApprovalRows([]);
    } catch (err) {
      console.error("Error fetching summary data", err);
      setSummaryData([]);
    }
  };

  const formatToYMD = (isoOrDate) => {
    if (!isoOrDate) return '';
    const d = new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // Image popup handlers
  const handleOpenImage = (imageUrl, title) => {
    const fullImageUrl = getFullImageUrl(imageUrl);
    if (fullImageUrl) {
      setImagePopup({
        open: true,
        imageUrl: fullImageUrl,
        title
      });
    }
  };

  const handleCloseImage = () => {
    setImagePopup({
      open: false,
      imageUrl: null,
      title: ''
    });
  };

  const handleDownloadImage = () => {
    if (imagePopup.imageUrl) {
      // Open image in new tab instead of downloading
      window.open(imagePopup.imageUrl, '_blank');
    }
  };

  // Helper component for image-enabled cells
  const ImageCell = ({ value, imageUrl, title }) => {
    const fullImageUrl = getFullImageUrl(imageUrl);
    const hasImage = !!fullImageUrl;
    const displayValue = value === '-' ? '-' : value;
    
    if (hasImage) {
      return (
        <Box 
          component="span"
          onClick={() => handleOpenImage(imageUrl, title)}
          sx={{ 
            color: 'primary.main',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontWeight: 'bold',
            '&:hover': { 
              color: 'primary.dark',
              textDecoration: 'underline'
            }
          }}
        >
          {displayValue}
        </Box>
      );
    }
    
    return <span>{displayValue}</span>;
  };

  const handleView = async (row) => {
    if (!row || !row.p_empcode) return;

    try {
      let startDate = form.startDate;
      let endDate = form.endDate;

      if ((!startDate || !endDate) && form.quickRange) {
        [startDate, endDate] = form.quickRange.split(",");
      }

      const payload = {
        startDate,
        endDate,
        emp_code: row.p_empcode,
        action: "admin",
        company_code: userInfo.company_code,
        emp_status: form.emp_status
      };

      setSelectedEmployee(row.p_employee_name);
      const resp = await fetchSummaryReport(payload);
      const details = resp || [];

      const enriched = details.map(r => ({
        ...r,
        status: r.status === "Rejected" ? "Rejected" : "Approved",
        admin_remark:  r.admin_remark ?? '',
        submitting: false,
        submitted: false,
        checked: false,
        approved_km_by_admin: r.approved_km_by_admin ?? r.p_approved_km_by_l2 ?? 0,
        approved_da_by_admin: r.approved_da_by_admin ?? r.p_approved_da_by_l2 ?? 0,
      }));

      setApprovalRows(enriched);
      setShowDetailTable(true);
    } catch (err) {
      console.error("Error fetching detail data", err);
      setApprovalRows([]);
      setShowDetailTable(false);
    }
  };

  const updateApprovalRow = (index, patch) => {
    setApprovalRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleSubmitSelected = async () => {
  const rowsToSubmit = approvalRows
    .map((r, idx) => ({ ...r, __idx: idx }))
    .filter(r => r.checked);

  if (rowsToSubmit.length === 0) {
    toast.error("No rows selected.");
    return;
  }

  let successCount = 0;
  let failCount = 0;

  for (const r of rowsToSubmit) {
    const idx = r.__idx;
    updateApprovalRow(idx, { submitting: true });

    const payload = {
      tr_id: Number(r.tr_id),
      emp_code: String(r.pp_emp_code),
      employee_name: String(r.employee_name),
      traveldate: String(formatToYMD(r.traveldate)),
      total_km: Number(r.total_km),
      total_daily_allowance: Number(r.total_daily_allowance),
      total_ta: Number(r.total_ta),
      total_parking_charges: Number(r.total_parking_charges),
      total_toll_charges: Number(r.total_toll_charges),
      total_hotel_charges: Number(r.total_hotel_charges),
      total_miss_charges: Number(r.total_miss_charges),
      total_public_transport: Number(r.p_total_publictransportcharges ?? 0),
      total_da_charges :  Number(r.p_total_dacharges ?? 0),
      grand_total: Number(r.grand_total),
      ex_remarks: String(r.p_ex_remarks || ""),
      remark:String(r.remark ?? ""),
      admin_remark: String(r.admin_remark ?? ""),
      status: String(r.status ?? ""),
      l1_updated_by: String(r.l1_updated_by ?? ""),
      approved_km_by_l1: Number(r.p_approved_km_by_l1 ?? 0),
      approved_da_by_l1: Number(r.p_approved_da_by_l1 ?? 0),
      l2_updated_by: String(r.l2_updated_by),
      l2_status: String(r.l2_status),
      l2_remark: String(r.p_l2_remark ?? ""),
      approved_km_by_l2: Number(r.p_approved_km_by_l2 ?? 0),
      approved_da_by_l2: Number(r.p_approved_da_by_l2 ?? 0),
      admin_updated_by:String(loggedInUser || ""),
      approved_km_by_admin: Number(r.approved_km_by_admin ?? 0),
      approved_da_by_admin: Number(r.approved_da_by_admin ?? 0),
    };

    try {
      await approveExpense(payload);
      updateApprovalRow(idx, { submitting: false, submitted: true, checked: false });
      successCount++;
    } catch (err) {
      console.error('Bulk submit error for idx', idx, err);
      updateApprovalRow(idx, { submitting: false });
      failCount++;
    }
  }

  if (successCount > 0) {
    toast.success(`${successCount} row(s) submitted successfully.`);
    handleFilterSubmit();
  }
  if (failCount > 0) toast.error(`${failCount} row(s) failed to submit.`);
};

  const summaryColumns = [
    { key: 'pending_expense_count', label: 'Pending' },
    {
      label: 'Actions',
      type: 'actions',
      actions: [
        {
          type: 'view',
          icon: <ViewIcon />,
          label: 'View',
          action: handleView,
        },
      ],
    },
    { key: 'p_empcode', label: 'Employee Code' },
    { key: 'p_employee_name', label: 'Employee Name' },
    { key: 'p_total_km', label: 'Total KM' },
    { key: 'p_total_daily_allowance', label: 'DA' },
    { key: 'p_total_ta', label: 'TA' },
    { key: 'p_total_parking_charges', label: 'Parking Charges' },
    { key: 'p_total_toll_charges', label: 'Toll Charges' },
    { key: 'p_total_hotel_charges', label: 'Hotel Charges' },
    { key: 'p_total_miss_charges', label: 'Misc Charges' },
    { key: 'p_total_dacharges', label: 'Convenience Charges' },
    { key: 'p_total_publictransportcharges', label: 'Public Transport' },
    { key: 'p_grand_total', label: 'Grand Total' },
  ];

  const totalCol = [
    { key: 'p_total_km', label: 'Total KM' },
    { key: 'p_total_daily_allowance', label: 'DA' },
    { key: 'p_total_ta', label: 'TA' },
    { key: 'p_total_parking_charges', label: 'Parking Charges' },
    { key: 'p_total_toll_charges', label: 'Toll Charges' },
    { key: 'p_total_hotel_charges', label: 'Hotel Charges' },
    { key: 'p_total_miss_charges', label: 'Misc Charges' },
    { key: 'p_total_dacharges', label: 'Convenience Charges' },
    { key: 'p_total_publictransportcharges', label: 'Public Transport' },
    { key: 'p_grand_total', label: 'Grand Total' },
  ]

  const filters = [
    {
      label: "Date Range",
      name: "quickRange",
      type: "select",
      options: [
        { label: "Select Date Range", value: "" },
        ...dateRanges
      ],
    },
  {
    label: "Emp Status",
    name: "emp_status",
    type: "select",
    options: [
      //{ label: "All", value: "ALL" },
      { label: "Active", value: "ACTIVE" },
      { label: "Inactive", value: "INACTIVE" },
    ],
  }
  ];

  const allChecked = approvalRows.length > 0 && approvalRows.every(r => !!r.checked);
  const anyCheckedSubmitting = approvalRows.some(r => r.checked && r.submitting);
  const anyChecked = approvalRows.some(r => r.checked);

  const toggleAll = (checked) => {
    setApprovalRows(prev => prev.map(r => ({ ...r, checked })));
  };

  // Updated column widths with short forms and smaller font size
  const tableColumns = [
    { label: "S.No", width: '35px' },
    { label: "Select", width: '50px' },
    { label: "Travel Date", width: '80px' },
    { label: "Aprd KM Adm", width: '70px', align: 'center' },
    { label: "Aprd DA Adm", width: '70px', align: 'center' },
    { label: "TA", width: '50px', align: 'center' },
    { label: "Parking", width: '60px', align: 'center' },
    { label: "Toll", width: '50px', align: 'center' },
    { label: "Hotel", width: '60px', align: 'center' },
    { label: "Misc", width: '50px', align: 'center' },
    { label: "Convenience", width: '70px', align: 'center' },
    { label: "Public Trans", width: '70px', align: 'center' },
    { label: "Grand Total", width: '70px', align: 'center' },
    { label: "Ex Remarks", width: '100px', align: 'right' },
    { label: "Travel Mode", width: '200px' },
    { label: "L1 Status", width: '70px' },
    { label: "L1 Remark", width: '100px' },
    { label: "L2 Status", width: '70px' },
    { label: "L2 Remark", width: '100px' },
    { label: "L1 App By", width: '80px'},
    { label: "L2 App By", width: '80px'},
    { label: "Status", width: '100px' },
    { label: "Remark", width: '200px' }, 
  ];

  const totals = approvalRows.reduce((acc, r) => {
    const sumField = (field) => {
      const v = r?.[field];
      const n = (v === null || v === undefined || v === '') ? 0 : Number(String(v).replace(/,/g, '')) || 0;
      return n;
    };
    acc.total_km += sumField('total_km');
    acc.total_daily_allowance += sumField('total_daily_allowance');
    acc.total_ta += sumField('total_ta');
    acc.total_parking_charges += sumField('total_parking_charges');
    acc.total_toll_charges += sumField('total_toll_charges');
    acc.total_hotel_charges += sumField('total_hotel_charges');
    acc.total_miss_charges += sumField('total_miss_charges');
    acc.p_total_da_raised += sumField('p_total_da_raised');
    acc.p_total_public_transport += sumField('p_total_public_transport');
    acc.grand_total += sumField('grand_total');

    acc.p_approved_km_by_l1 += sumField('p_approved_km_by_l1');
    acc.p_approved_da_by_l1 += sumField('p_approved_da_by_l1');
    acc.p_approved_km_by_l2 += sumField('p_approved_km_by_l2');
    acc.p_approved_da_by_l2 += sumField('p_approved_da_by_l2');
    acc.approved_km_by_admin += sumField('approved_km_by_admin');
    acc.approved_da_by_admin += sumField('approved_da_by_admin');
    return acc;
  }, {
    total_km: 0,
    total_daily_allowance: 0,
    total_ta: 0,
    total_parking_charges: 0,
    total_toll_charges: 0,
    total_hotel_charges: 0,
    total_miss_charges: 0,
    p_total_da_raised: 0,
    p_total_public_transport: 0,
    grand_total: 0,
    p_approved_km_by_l1: 0,
    p_approved_da_by_l1: 0,
    p_approved_km_by_l2: 0,
    p_approved_da_by_l2: 0,
    approved_km_by_admin:0,
    approved_da_by_admin:0
  });

  const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString() : '-');


  return (
    <Box>
      {/* Image Popup Dialog */}
      <Dialog 
        open={imagePopup.open} 
        onClose={handleCloseImage}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{imagePopup.title}</DialogTitle>
        <DialogContent>
          {imagePopup.imageUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <img 
                src={imagePopup.imageUrl} 
                alt={imagePopup.title}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '60vh',
                  objectFit: 'contain'
                }} 
                onError={(e) => {
                  console.error('Error loading image:', imagePopup.imageUrl);
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImage}>Close</Button>
          <Button 
            onClick={handleDownloadImage} 
            variant="contained"
            disabled={!imagePopup.imageUrl}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {!showDetailTable ? (
        <MantisDataTable
          title="Expense Summary Report Admin"
          searchable
          searchPlaceholder='Search by Name, Code'
          data={summaryData}
          columns={summaryColumns}
          filters={filters}
          onFilterChange={handleFilterChange}
          onFilterSubmit={handleFilterSubmit}
          footerTotals={calculateTotals(totalCol, summaryData)} 
        />
      ) : (
        <Box>
          {/* Breadcrumb navigation */}
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link
              component="button"
              color="inherit"
              onClick={() => setShowDetailTable(false)}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <ArrowBackIcon sx={{ mr: 0.5 }} fontSize="small" />
              Back to Summary
            </Link>
            <Typography color="text.primary">Expense Details Admin Approval</Typography>
          </Breadcrumbs>

          <Paper sx={{ p: 2, overflow: 'auto' }}>
            <Typography variant="h5" gutterBottom>
              Expense Approval Admin
            </Typography>

            <Typography variant="h6" gutterBottom>
              Travel detailed summary of{" "}
              <span style={{ color: '#1976d2' }}>
                {selectedEmployee || '-'}
              </span>
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {form.startDate || form.endDate
                ? `from ${formatToYMD(form.startDate || '') || '-'} to ${formatToYMD(form.endDate || '') || '-'}`
                : ''}
            </Typography>

          <TableContainer sx={{ mt: 2, maxWidth: '100%', overflowX: 'auto', maxHeight: '70vh' }}>
            <Table stickyHeader sx={{ minWidth: 1100, fontSize: '0.7rem' }}>
                <TableHead>
                  <TableRow>
                    {tableColumns.map((column, index) => (
                      <TableCell
                        key={index}
                        align={column.align || 'left'}
                        sx={{ 
                          width: column.width, 
                          fontWeight: 'bold',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                          backgroundColor: "#c8e0f8ff", 
                          color: "black",
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          fontSize: '0.65rem',
                          lineHeight: '1.1',
                          padding: '6px 3px',
                        }}
                      >
                        {column.label === "Select" ? (
                          <Checkbox
                            checked={allChecked}
                            onChange={(e) => toggleAll(e.target.checked)}
                            size="small"
                            sx={{ padding: '2px' }}
                          />
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {approvalRows.map((row, index) => (
                      <TableRow key={index} sx={{ fontSize: '0.7rem' }}>
                        <TableCell align="center" sx={{ width: '35px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          {index + 1}
                        </TableCell>

                        <TableCell sx={{ width: '50px', padding: '6px 3px' }}>
                          <Checkbox
                            checked={!!row.checked}
                            disabled={row.submitting || row.submitted}
                            onChange={(e) => updateApprovalRow(
                              index,
                              { checked: e.target.checked }
                            )}
                            size="small"
                            sx={{ padding: '2px' }}
                          />
                        </TableCell>

                        <TableCell sx={{ width: '80px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          {formatToYMD(row.traveldate || '')}
                        </TableCell>
                        
                        <TableCell align="center" sx={{ width: '70px', padding: '6px 3px' }}>
                          <TextField
                            type="number"
                            size="small"
                            value={row.approved_km_by_admin || ''}
                            disabled={row.submitting || row.submitted}
                            onChange={(e) =>
                              updateApprovalRow(index, {
                                approved_km_by_admin: e.target.value
                              })
                            }
                            sx={{ 
                              width: '65px', 
                              '& .MuiInputBase-input': { 
                                fontSize: '0.7rem', 
                                padding: '3px',
                                height: '24px'
                              } 
                            }}
                            inputProps={{ 
                              style: { 
                                fontSize: '0.7rem',
                                height: '24px'
                              } 
                            }}
                          />
                        </TableCell>

                        <TableCell align="center" sx={{ width: '70px', padding: '6px 3px' }}>
                          <TextField
                            type="number"
                            size="small"
                            value={row.approved_da_by_admin || ''}
                            disabled={row.submitting || row.submitted}
                            onChange={(e) =>
                              updateApprovalRow(index, {
                                approved_da_by_admin: e.target.value
                              })
                            }
                            sx={{ 
                              width: '65px', 
                              '& .MuiInputBase-input': { 
                                fontSize: '0.7rem', 
                                padding: '3px',
                                height: '24px'
                              } 
                            }}
                            inputProps={{ 
                              style: { 
                                fontSize: '0.7rem',
                                height: '24px'
                              } 
                            }}
                          />
                        </TableCell>

                        <TableCell align="center" sx={{ width: '50px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          {fmt(row.total_ta)}
                        </TableCell>

                        {/* Parking Charges with Image */}
                        <TableCell align="center" sx={{ width: '60px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          <ImageCell 
                            value={fmt(row.total_parking_charges)}
                            imageUrl={row.p_parking_charges_img}
                            title="Parking Charges Receipt"
                          />
                        </TableCell>

                        {/* Toll Charges with Image */}
                        <TableCell align="center" sx={{ width: '50px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          <ImageCell 
                            value={fmt(row.total_toll_charges)}
                            imageUrl={row.p_toll_charges_img}
                            title="Toll Charges Receipt"
                          />
                        </TableCell>

                        {/* Hotel Charges with Image */}
                        <TableCell align="center" sx={{ width: '60px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          <ImageCell 
                            value={fmt(row.total_hotel_charges)}
                            imageUrl={row.p_hotel_charges_img}
                            title="Hotel Charges Receipt"
                          />
                        </TableCell>

                        {/* Misc Charges with Image */}
                        <TableCell align="center" sx={{ width: '50px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          <ImageCell 
                            value={fmt(row.total_miss_charges)}
                            imageUrl={row.p_miss_charges_img}
                            title="Miscellaneous Charges Receipt"
                          />
                        </TableCell>

                        <TableCell align="center" sx={{ width: '70px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          {fmt(row.p_total_da_raised)}
                        </TableCell>
                                                
                        {/* Public Transport with Image */}
                        <TableCell align="center" sx={{ width: '70px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          <ImageCell 
                            value={fmt(row.p_total_public_transport)}
                            imageUrl={row.p_publictransportchargesimg}
                            title="Public Transport Receipt"
                          />
                        </TableCell>

                        <TableCell align="center" sx={{ width: '70px', fontSize: '0.7rem', padding: '6px 3px', fontWeight: 'bold' }}>
                          {fmt(row.grand_total)}
                        </TableCell>
                        <TableCell sx={{ width: '380px', padding: '6px 8px' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.72rem',
                              lineHeight: '1.2',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              color: '#444'
                            }}
                          >
                            {row.p_ex_remarks || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: '280px', padding: '6px 8px' }}>
                                                                          <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                              fontSize: '0.72rem',
                                                                              lineHeight: '1.2',
                                                                              whiteSpace: 'pre-wrap',
                                                                              wordBreak: 'break-word',
                                                                              color: '#444'
                                                                            }}
                                                                          >
                                                                            {row.travel_mode || '-'}
                                                                          </Typography>
                                                                        </TableCell>
                        <TableCell sx={{ width: '70px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          {row.status}
                        </TableCell>
                        <TableCell sx={{ width: '100px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          {row.remark}
                        </TableCell>
                        
                        <TableCell sx={{ width: '70px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          {row.l2_status}
                        </TableCell>
                        <TableCell sx={{ width: '100px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          {row.p_l2_remark}
                        </TableCell>
                        <TableCell sx={{ width: '80px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          {row.l1_approver_name}
                        </TableCell>
                        <TableCell sx={{ width: '80px', fontSize: '0.7rem', padding: '6px 3px' }}>
                          {row.l2_approver_name}
                        </TableCell>
                        
                        <TableCell sx={{ width: '100px', padding: '6px 3px' }}>
                          <Box display="flex" flexDirection="column" gap={0.3}>
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={row.status === "Approved"}
                                  disabled={row.submitting || row.submitted}
                                  onChange={() => updateApprovalRow(
                                    index,
                                    { status: "Approved" }
                                  )}
                                  size="small"
                                  sx={{ padding: '2px' }}
                                />
                              }
                              label="Approve"
                              sx={{ 
                                fontSize: '0.7rem', 
                                margin: 0,
                                '& .MuiFormControlLabel-label': { 
                                  fontSize: '0.65rem',
                                  lineHeight: '1.1'
                                }
                              }}
                            />
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={row.status === "Rejected"}
                                  disabled={row.submitting || row.submitted}
                                  onChange={() => updateApprovalRow(
                                    index,
                                    { status: "Rejected" }
                                  )}
                                  size="small"
                                  sx={{ padding: '2px' }}
                                />
                              }
                              label="Reject"
                              sx={{ 
                                fontSize: '0.7rem', 
                                margin: 0,
                                '& .MuiFormControlLabel-label': { 
                                  fontSize: '0.65rem',
                                  lineHeight: '1.1'
                                }
                              }}
                            />
                          </Box>
                        </TableCell>

                        <TableCell sx={{ width: '200px', padding: '6px 3px' }}>
                          <TextField
                            multiline
                            rows={2}
                            fullWidth
                            value={row.admin_remark}
                            placeholder="Enter reason"
                            disabled={row.submitting || row.submitted}
                            onChange={(e) => updateApprovalRow(
                              index,
                              { admin_remark: e.target.value }
                            )}
                            sx={{ 
                              minWidth: '190px', 
                              '& .MuiInputBase-input': { 
                                fontSize: '0.7rem',
                                lineHeight: '1.1',
                                padding: '4px'
                              } 
                            }}
                            inputProps={{ style: { fontSize: '0.7rem' } }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>

                {/* Totals row */}
                <TableBody>
                  <TableRow 
                   sx={{ 
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          backgroundColor: "#c8e0f8ff", 
                          color: "black",
                          position: "sticky",
                          bottom: 0,
                          zIndex: 2,
                          fontSize: '0.7rem'
                        }}
                  >
                    <TableCell colSpan={3} sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      Totals
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      {fmt(totals.approved_km_by_admin)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      {fmt(totals.approved_da_by_admin)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      {fmt(totals.total_ta)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      {fmt(totals.total_parking_charges)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      {fmt(totals.total_toll_charges)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      {fmt(totals.total_hotel_charges)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      {fmt(totals.total_miss_charges)}
                    </TableCell>

                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      {fmt(totals.p_total_da_raised)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      {fmt(totals.p_total_public_transport)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px 3px' }}>
                      {fmt(totals.grand_total)}
                    </TableCell>
                    <TableCell colSpan={8} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={handleSubmitSelected}
                variant="contained"
                disabled={!anyChecked || anyCheckedSubmitting}
                startIcon={anyCheckedSubmitting ? <CircularProgress size={14} /> : null}
                sx={{ mr: 2, fontSize: '0.7rem', py: 0.4, px: 1.5 }}
              >
                {anyCheckedSubmitting ? 'Submitting...' : 'Submit Selected'}
              </Button>
              <Button 
                onClick={() => setShowDetailTable(false)}
                variant="outlined"
                sx={{ fontSize: '0.7rem', py: 0.4, px: 1.5 }}
              >
                Back to Summary
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default AdminApproval;